import { useEffect, useState } from "react";
import { api } from "../../api";
import { Btn, Card, Badge, ProgressBar, Field, Input, PageHead, StatTile } from "../../components/ui";
import Icon from "../../components/Icon";
import { BRAND, BRAND_SOFT, BRAND_TINT } from "../../theme";

function getAvailableDates(milestone) {
  const targetDay = milestone === 50 ? 3 : 4;
  const dates = [];
  const now = new Date();
  for (let m = 0; m < 6 && dates.length < 2; m++) {
    const year = now.getFullYear();
    const month = now.getMonth() + m;
    const d = new Date(year, month, 1);
    let count = 0;
    while (d.getMonth() === (month % 12)) {
      if (d.getDay() === targetDay) {
        count++;
        if (count === 2 || count === 4) {
          if (d > now) { dates.push(new Date(d)); if (dates.length >= 2) break; }
        }
      }
      d.setDate(d.getDate() + 1);
    }
  }
  return dates;
}

function RequestModal({ milestone, onClose, onDone, toast }) {
  const [selected, setSelected] = useState("");
  const available = getAvailableDates(milestone);
  const submit = async () => {
    try { await api.requestSession({ milestone, requested_date: selected }); toast("Session booked successfully!"); onDone(); }
    catch (e) { toast(e.message); }
  };
  const dayName = milestone === 50 ? "Wednesday" : "Thursday";
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/50 p-4" onClick={onClose}>
      <Card className="w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-900">Book your {milestone}% interactive session</h2>
          <button onClick={onClose} className="text-slate-400"><Icon name="x" size={18} /></button>
        </div>
        <p className="text-sm text-slate-600 mb-4">Sessions are held on the <b>2nd and 4th {dayName}</b> of each month. Select an available date below:</p>
        <div className="space-y-2 max-h-48 overflow-y-auto mb-4">
          {available.map((d) => {
            const val = d.toISOString().split("T")[0];
            const label = d.toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" });
            return (
              <label key={val} className={`flex items-center gap-3 rounded-lg border px-4 py-3 cursor-pointer ${selected === val ? "border-indigo-500 bg-indigo-50" : "border-slate-200 hover:bg-slate-50"}`}>
                <input type="radio" name="session-date" checked={selected === val} onChange={() => setSelected(val)} style={{ accentColor: "#4F46E5" }} />
                <span className="text-sm text-slate-800">{label}</span>
              </label>
            );
          })}
          {available.length === 0 && <p className="text-sm text-slate-400">No available dates in the next 3 months.</p>}
        </div>
        <div className="rounded-lg bg-amber-50 border border-amber-100 px-4 py-3 mb-4 space-y-2">
          <div className="flex items-start gap-2">
            <span className="text-amber-500 mt-0.5">•</span>
            <p className="text-sm text-amber-800">The live session will begin at <b>7:00 PM IST</b>. You will receive the session link via email.</p>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-amber-500 mt-0.5">•</span>
            <p className="text-sm text-amber-800">Feel free to continue with the remaining modules — there's no need to wait for the live session to keep progressing.</p>
          </div>
        </div>
        <div className="flex justify-end gap-2"><Btn variant="outline" onClick={onClose}>Cancel</Btn><Btn onClick={submit} disabled={!selected}>Confirm booking</Btn></div>
      </Card>
    </div>
  );
}

export default function Overview({ toast }) {
  const [courses, setCourses] = useState([]);
  const [activeCourseId, setActiveCourseId] = useState(null);
  const [course, setCourse] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [modal, setModal] = useState(null);

  const load = () => {
    api.myCourses().then(setCourses).catch(() => {});
    api.myCourse().then(setCourse).catch(() => {});
    api.mySessions().then(setSessions).catch(() => {});
  };
  useEffect(() => { if (activeCourseId || courses.length <= 1) load(); }, [activeCourseId]);
  if (!course) return <div className="text-slate-400">Loading…</div>;

  return (
    <div>
      {courses.length > 1 && (
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          {courses.map((c) => (
            <button key={c.id} onClick={() => { setActiveCourseId(c.id); localStorage.setItem("activeCourseId", c.id); }}
              className="rounded-lg border px-4 py-2 text-sm font-medium"
              style={activeCourseId === c.id ? { backgroundColor: BRAND, color: "#fff", borderColor: BRAND } : { borderColor: "#d6d3d1", color: "#57534e" }}>
              {c.name}
            </button>
          ))}
        </div>
      )}
      <PageHead title="Overview" sub={`Access ends ${new Date(course.expires_at).toLocaleDateString()}`} />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        {[["Course progress", `${course.progress}%`, "dashboard"],
          ["Lessons", `${course.modules.reduce((a, m) => a + (m.completed_count || 0), 0)} / ${course.modules.reduce((a, m) => a + (m.sub_count || 0), 0)}`, "book"],
          ["Sessions requested", `${sessions.length}`, "calendar"]].map(([l, v, ic]) => (
          <Card key={l} className="p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: BRAND_SOFT, color: BRAND }}><Icon name={ic} size={20} /></div>
            <div><div className="text-sm text-slate-500">{l}</div><div className="text-2xl font-bold text-slate-900">{v}</div></div>
          </Card>
        ))}
      </div>

      <Card className="p-0 mb-4 overflow-hidden">
        {course.thumbnail_url
          ? <div className="h-36 bg-cover bg-center relative" style={{ backgroundImage: `url(${api.fileUrl(course.thumbnail_url)})` }}>
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              <div className="absolute bottom-0 left-0 px-5 pb-3 text-white font-semibold text-lg">{course.course_name}</div>
            </div>
          : <div className="px-5 pt-5 text-sm font-medium text-slate-700">{course.course_name}</div>}
        <div className="p-5">
        <ProgressBar value={course.progress} />
        {course.sessions_enabled !== false && <div className="flex flex-wrap items-center gap-3 mt-4">
          <Btn disabled={!course.session_50_unlocked} onClick={() => setModal(50)}>
            <Icon name="calendar" size={16} />Request interactive session (50%)
          </Btn>
          <Btn variant="outline" disabled={!course.session_100_unlocked} onClick={() => setModal(100)}>
            <Icon name="award" size={16} />Request second session (100%)
          </Btn>
          {!course.session_50_unlocked && <span className="text-xs text-slate-500">Reach 50% to unlock session booking.</span>}
        </div>}
        </div>
      </Card>

      {course.sessions_enabled !== false && <Card className="p-5">
        <div className="text-sm font-semibold text-slate-700 mb-3">Your session requests</div>
        {sessions.length === 0 && <div className="text-sm text-slate-400">No requests yet.</div>}
        <div className="space-y-2">
          {sessions.map((s) => (
            <div key={s.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
              <div>
                <div className="text-sm text-slate-800">{s.milestone}% session · requested {s.requested_date}</div>
                {s.assigned_date && <div className="text-xs text-slate-500">Confirmed {s.assigned_date}{s.meeting_link ? ` · ${s.meeting_link}` : ""}</div>}
              </div>
              <Badge>{s.status}</Badge>
            </div>
          ))}
        </div>
      </Card>}

      {modal && <RequestModal milestone={modal} onClose={() => setModal(null)} toast={toast} onDone={() => { setModal(null); load(); }} />}
    </div>
  );
}
